import { useEffect, useState } from "react";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard React examples

// Custom styles for DashboardNavbar
import { navbar, navbarContainer, navbarRow } from "examples/Navbars/DashboardNavbar/styles";

// Soft UI Dashboard React context
import { setTransparentNavbar, useSoftUIController } from "context";

// Images
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import backgrounImage from "assets/images/backgroundImage.png";
import defaultuser from "assets/images/defaultuserimage.png";
import SoftAvatar from "components/SoftAvatar";
import useStore from "store/hook";
import collapseIcon from "assets/images/collapseicon.png";
import logoutIcon from "assets/images/logout.png";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const navigate = useNavigate();
  const location = useLocation();
  const { setDialogue, store } = useStore();
  const [open, setOpen] = useState(false);
  const { isCollapse } = store?.app?.dialogs?.collapseSidenav || {};

  const detailRoutePrefixes = [
    "/user-details/",
    "/school-details/",
    "/city-details/",
    "/edit-school/",
    "/edit-city/",
    "/add-school",
    "/add-city",
  ];
  const returnPath = location.state?.from;
  const isDetailPage = detailRoutePrefixes.some((prefix) => location.pathname.startsWith(prefix));

  const handleHeaderIconClick = () => {
    if (isDetailPage && returnPath) {
      navigate(returnPath);
      return;
    }

    setDialogue({
      type: "collapseSidenav",
      isCollapse: !isCollapse,
      action: "",
    });
  };

  // const [logOutFunc] = useLogoutMutation();

  const logOutFunc = () => {
    localStorage.removeItem("loginData");
    navigate("/authentication/sign-in");
  };

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  let { pathname } = location;
  pathname = location.pathname.split("/")[1];

  const routeMapping = {
    dashboard: "Dashboard",
    users: "Users",
    "user-details": "Users",
    "reported-users": "Reported Users",
    "add-school": "Schools",
    "school-details": "School Details",
    "edit-school": "Edit Schools",
    schools: "Schools",
    cities: "Cities",
    "city-details": "Cities",
    "add-city": "Cities",
    "edit-city": "Edit City",
  };

  const collapseKey = Object.keys(routeMapping).find((route) => pathname.startsWith(route));

  const collapseName = collapseKey ? routeMapping[collapseKey] : null;

  return (
    <SoftBox
      sx={{
        position: "relative",
        width: "100%",
        height: "290px",
        backgroundImage: `url(${backgrounImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        pl: 1,
      }}
    >
      <AppBar
        position={"relative"}
        sx={(theme) => ({
          ...navbar(theme, { absolute, light }),
          backgroundColor: theme.palette.primary.main,
          boxShadow: "none",
        })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme)}>
          <SoftBox
            color="inherit"
            mb={{ xs: 1, md: 0 }}
            sx={(theme) => navbarRow(theme, { miniSidenav })}
          >
            {miniSidenav && (
              <SoftBox
                onClick={() =>
                  setDialogue({
                    type: "toggleSidebar",
                    isOpen: true,
                  })
                }
              >
                <IconButton>
                  <MenuIcon sx={{ color: "white.main", fontWeight: "bold" }} />
                </IconButton>
              </SoftBox>
            )}

            <SoftBox display="flex" alignItems="center" justifyContent="center" gap={1}>
              {!miniSidenav && (
                <SoftBox
                  onClick={handleHeaderIconClick}
                  height="20px"
                  width="20px"
                  sx={{
                    backgroundImage: `url(${collapseIcon})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "contain",
                    cursor: "pointer",
                    p: 1,
                  }}
                />
              )}

              <SoftTypography fontWeight="normal" color="white" mr={2} sx={{ fontSize: 22 }}>
                {collapseName}
              </SoftTypography>
            </SoftBox>
          </SoftBox>

          {miniSidenav ? (
            <SoftBox
              sx={{ width: { xs: "100%", sm: "30%", md: "20%" } }}
              width="100%"
              color={light ? "white" : "inherit"}
            >
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                position="relative"
                borderRadius="xxl"
                p={1}
                gap={2}
                sx={{ backgroundColor: "white.main", cursor: "pointer" }}
                onClick={() => setOpen((prev) => !prev)}
              >
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="xxl"
                  gap={1}
                >
                  <SoftAvatar
                    sx={{
                      cursor: "pointer",
                      transition: "transform 0.2s ease-in-out",
                      borderRadius: "50%",
                    }}
                    src={defaultuser}
                    alt="logout"
                    size="sm"
                    variant="rounded"
                  />

                  <SoftTypography fontWeight="normal" color="dark" sx={{ fontSize: 14 }}>
                    Admin
                  </SoftTypography>
                </SoftBox>

                <KeyboardArrowDownIcon />

                {open && (
                  <SoftBox
                    position="absolute"
                    top="85%"
                    left={8}
                    width="90%"
                    height="40px"
                    display="flex"
                    gap={1}
                    alignItems="center"
                    justifyContent="start"
                    px={1}
                    boxShadow="md"
                    sx={{
                      borderBottomLeftRadius: "10px",
                      borderBottomRightRadius: "10px",
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      backgroundColor: "white.main",
                      zIndex: 10,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDialogue({
                        type: "logout",
                        isOpen: true,
                        action: "",
                      });
                    }}
                  >
                    <SoftBox
                      height="20px"
                      width="20px"
                      sx={{
                        backgroundImage: `url(${logoutIcon})`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        backgroundSize: "contain",
                        cursor: "pointer",
                        p: 1,
                      }}
                    />

                    <SoftTypography fontWeight="normal" color="dark" sx={{ fontSize: 14 }}>
                      Logout
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>
            </SoftBox>
          ) : (
            <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
              <SoftBox pr={2} color={light ? "white" : "inherit"}>
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  position="relative"
                  borderRadius="xxl"
                  p={1}
                  gap={2}
                  sx={{ backgroundColor: "white.main", cursor: "pointer" }}
                  onClick={() => setOpen((prev) => !prev)}
                >
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="xxl"
                    gap={1}
                  >
                    <SoftAvatar
                      sx={{
                        cursor: "pointer",
                        transition: "transform 0.2s ease-in-out",
                        borderRadius: "50%",
                      }}
                      src={defaultuser}
                      alt="logout"
                      size="sm"
                      variant="rounded"
                    />

                    <SoftTypography fontWeight="normal" color="dark" sx={{ fontSize: 14 }}>
                      Admin
                    </SoftTypography>
                  </SoftBox>

                  <KeyboardArrowDownIcon />

                  {open && (
                    <SoftBox
                      position="absolute"
                      top="85%"
                      left={8}
                      width="90%"
                      height="40px"
                      display="flex"
                      gap={1}
                      alignItems="center"
                      justifyContent="start"
                      px={1}
                      boxShadow="md"
                      sx={{
                        borderBottomLeftRadius: "10px",
                        borderBottomRightRadius: "10px",
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        backgroundColor: "white.main",
                        zIndex: 10,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setDialogue({
                          type: "logout",
                          isOpen: true,
                          action: "",
                        });
                      }}
                    >
                      <SoftBox
                        height="20px"
                        width="20px"
                        sx={{
                          backgroundImage: `url(${logoutIcon})`,
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                          backgroundSize: "contain",
                          cursor: "pointer",
                          p: 1,
                        }}
                      />

                      <SoftTypography fontWeight="normal" color="dark" sx={{ fontSize: 14 }}>
                        Logout
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          )}
        </Toolbar>
      </AppBar>
    </SoftBox>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
